package com.swetdot.swetgram.data.picso

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.domain.PicsoFeedTab
import com.swetdot.swetgram.protocol.dto.*
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import kotlin.time.Clock
import kotlin.time.Instant
import kotlin.time.TimeSource

/** A server-clock anchor survives device clock changes for the lifetime of this session. */
class PicsoClock {
    private var server: Instant? = null
    private var mark = TimeSource.Monotonic.markNow()

    fun synchronize(timestamp: String?) {
        val parsed = timestamp?.let { runCatching { Instant.parse(it) }.getOrNull() } ?: return
        val current = server?.plus(mark.elapsedNow())
        // A slower response may contain an earlier timestamp than one already received.
        // Once synchronized, never rewind the expiry clock for an out-of-order response.
        server = if (current == null || parsed > current) parsed else current
        mark = TimeSource.Monotonic.markNow()
    }

    fun now(): Instant = server?.plus(mark.elapsedNow()) ?: Clock.System.now()
}

interface PicsoSource {
    fun now(): Instant
    suspend fun getFeed(feedTab: PicsoFeedTab = PicsoFeedTab.ALL, cursor: String? = null, limit: Int = 20): PicsoFeedPage
    suspend fun getSaved(cursor: String? = null, limit: Int = 20): PicsoFeedPage
    suspend fun getPost(id: String): PicsoPost
    suspend fun image(id: String): ByteArray
    suspend fun create(uploadId: String, caption: String?, clientId: String): PicsoPost
    suspend fun setReaction(picsoId: String, emoji: String): PicsoReactionSummary
    suspend fun removeReaction(picsoId: String): PicsoReactionSummary
    suspend fun save(picsoId: String): PicsoSaveStatus
    suspend fun unsave(picsoId: String): PicsoSaveStatus
    suspend fun recordView(picsoId: String, clientId: String)
    suspend fun delete(picsoId: String)
    suspend fun getAnalytics(picsoId: String): PicsoAnalytics
    suspend fun share(picsoId: String, conversationId: String, clientId: String): Message
}

class PicsoRepository(private val api: ApiClient) : PicsoSource {
    private val clock = PicsoClock()
    override fun now(): Instant = clock.now()

    override suspend fun getFeed(feedTab: PicsoFeedTab, cursor: String?, limit: Int): PicsoFeedPage =
        api.get<PicsoFeedPage>("picso/feed") {
            cursor?.let { parameter("cursor", it) }
            parameter("limit", limit)
        }.also { clock.synchronize(it.serverTime) }

    override suspend fun getSaved(cursor: String?, limit: Int): PicsoFeedPage =
        api.get<PicsoFeedPage>("picso/saved") {
            cursor?.let { parameter("cursor", it) }
            parameter("limit", limit)
        }.also { clock.synchronize(it.serverTime) }

    override suspend fun getPost(id: String): PicsoPost =
        api.get<PicsoPost>("picso/$id").also { clock.synchronize(it.serverTime) }

    // Never load media_url: private Picso bytes must be authorized at every network read.
    override suspend fun image(id: String): ByteArray = api.getBytes("picso/$id/image")

    override suspend fun create(uploadId: String, caption: String?, clientId: String): PicsoPost =
        api.post<PicsoPost>("picso", idempotencyKey = clientId) {
            setBody(CreatePicsoRequest(clientId, uploadId, caption?.takeIf { it.isNotBlank() }))
        }.also { clock.synchronize(it.serverTime) }

    override suspend fun setReaction(picsoId: String, emoji: String): PicsoReactionSummary =
        api.post("picso/$picsoId/reactions") { setBody(SetPicsoReactionRequest(emoji)) }

    override suspend fun removeReaction(picsoId: String): PicsoReactionSummary = api.delete("picso/$picsoId/reactions")
    override suspend fun save(picsoId: String): PicsoSaveStatus = api.put("picso/$picsoId/save")
    override suspend fun unsave(picsoId: String): PicsoSaveStatus = api.delete("picso/$picsoId/save")
    override suspend fun recordView(picsoId: String, clientId: String) {
        api.post<Unit>("picso/$picsoId/views", idempotencyKey = clientId) {
            setBody(RecordPicsoViewRequest(clientId))
        }
    }

    override suspend fun delete(picsoId: String) { api.delete<Unit>("picso/$picsoId") }
    override suspend fun getAnalytics(picsoId: String): PicsoAnalytics = api.get("picso/$picsoId/analytics")
    override suspend fun share(picsoId: String, conversationId: String, clientId: String): Message =
        api.post("picso/$picsoId/share", idempotencyKey = clientId) {
            setBody(PicsoShareRequest(clientId, conversationId))
        }
}
